/**
 * User router
 * @package @app/api
 */

import {
  parseJsonArray,
  serializeJsonArrayFields,
  userUpdateInputSchema,
} from '@app/validators/schemas';
import { captureServerEvent } from '../lib/analytics';
import { protectedProcedure } from '../middleware/auth';

export const userRouter = {
  me: protectedProcedure.handler(async ({ context }) => {
    const { db, session } = context;

    // Org staff lookup runs in parallel with the user fetch so the
    // banner/onboarding gate downstream can suppress the athlete-only
    // flows when the caller is an owner/admin/compliance officer.
    const staffMembership = await db.member.findFirst({
      where: {
        userId: session.user.id,
        role: { in: ['owner', 'admin', 'compliance'] },
      },
      select: { role: true },
    });

    const user = await db.user.findUnique({
      where: { id: session.user.id },
      select: {
        id: true,
        name: true,
        email: true,
        image: true,
        role: true,
        sport: true,
        school: true,
        socialFollowers: true,
        socialEngagement: true,
        phoneNumber: true,
        language: true,

        // US-023: athletic info
        position: true,
        classYear: true,
        conference: true,
        division: true,
        hometown: true,

        // US-023: personal brand
        bio: true,
        personalValues: true,
        interests: true,
        causes: true,

        // US-023: per-platform social
        instagramHandle: true,
        instagramFollowers: true,
        tiktokHandle: true,
        tiktokFollowers: true,
        twitterHandle: true,
        twitterFollowers: true,
        youtubeHandle: true,
        youtubeFollowers: true,

        // US-023: deal preferences
        preferredDealTypes: true,
        preferredIndustries: true,
        minimumDealSize: true,
        openToDeals: true,

        // US-031: NCAA eligibility — verification fields are read-only here;
        // they are written only by the compliance attestation flow.
        ncaaId: true,
        ncaaIdAddedAt: true,
        eligibilityVerifiedAt: true,
        eligibilityVerifiedBy: true,
        eligibilityVerificationSource: true,
        onboardingNcaaIdSkippedAt: true,
        userType: true,
      },
    });

    if (!user) throw new Error('User not found');

    // Parse JSON-string array fields so the client works with real arrays.
    return {
      ...user,
      minimumDealSize: user.minimumDealSize ? Number(user.minimumDealSize) : null,
      personalValues: parseJsonArray(user.personalValues),
      interests: parseJsonArray(user.interests),
      causes: parseJsonArray(user.causes),
      preferredDealTypes: parseJsonArray(user.preferredDealTypes),
      preferredIndustries: parseJsonArray(user.preferredIndustries),
      // US-031: serialize the eligibility timestamps consistently with the
      // rest of our API (ISO strings, never Date objects across the wire).
      ncaaIdAddedAt: user.ncaaIdAddedAt?.toISOString() ?? null,
      eligibilityVerifiedAt: user.eligibilityVerifiedAt?.toISOString() ?? null,
      onboardingNcaaIdSkippedAt: user.onboardingNcaaIdSkippedAt?.toISOString() ?? null,
      // Compliance / admin / owner of any org → don't pester them with the
      // athlete-only NCAA flows. They can still add an NCAA ID in /profile
      // if they're ALSO an athlete; we just don't block or banner them.
      isOrgStaff: !!staffMembership,
    };
  }),

  update: protectedProcedure.input(userUpdateInputSchema).handler(async ({ input, context }) => {
    const data = serializeJsonArrayFields(input);

    // US-031: stamp `ncaaIdAddedAt` the first time an athlete fills the
    // field, and clear the "skipped onboarding" flag so the banner stops
    // nagging. Empty/null `ncaaId` clears the timestamp (treat as "not
    // set yet"). All other write effects on eligibility-related fields
    // are server-managed — the schema doesn't accept them as input.
    if (Object.hasOwn(input, 'ncaaId')) {
      const ncaaId = (data as Record<string, unknown>).ncaaId;
      if (ncaaId && typeof ncaaId === 'string' && ncaaId.length > 0) {
        // Read the current row to know if this is the FIRST time the field
        // is being set; only then do we stamp ncaaIdAddedAt.
        const existing = await context.db.user.findUnique({
          where: { id: context.session.user.id },
          select: { ncaaIdAddedAt: true },
        });
        if (!existing?.ncaaIdAddedAt) {
          (data as Record<string, unknown>).ncaaIdAddedAt = new Date();
        }
        // The athlete has filled the field; clear any "I'll skip for now"
        // mark so the banner disappears.
        (data as Record<string, unknown>).onboardingNcaaIdSkippedAt = null;
      } else {
        // Cleared — reset the added-at stamp so a future re-fill triggers
        // the "first time" path again. Leaves verification fields alone:
        // those are compliance-side and shouldn't be wiped by an athlete
        // clearing their own ID.
        (data as Record<string, unknown>).ncaaIdAddedAt = null;
        (data as Record<string, unknown>).ncaaId = null;
      }
    }
    // Coerce ISO string → Date for the skip stamp when the client sends it.
    if (
      Object.hasOwn(input, 'onboardingNcaaIdSkippedAt') &&
      typeof input.onboardingNcaaIdSkippedAt === 'string'
    ) {
      (data as Record<string, unknown>).onboardingNcaaIdSkippedAt = new Date(
        input.onboardingNcaaIdSkippedAt,
      );
    }

    await context.db.user.update({
      where: { id: context.session.user.id },
      data,
    });

    await captureServerEvent({
      distinctId: context.session.user.id,
      event: 'profile_updated',
      properties: { fieldsChanged: Object.keys(input) },
    });

    return { success: true };
  }),
};
